import { Controller, useFormContext, useFieldArray } from 'react-hook-form';
import { Plus, Trash2 } from 'lucide-react';
import { Button, Input, CardTitle } from '@vnetwork/ui/primitives';
import { Field } from '@vnetwork/ui/forms';
import type { ContractFormValues } from '../contract-form';
import { NumericInput } from '@/components/numeric-input';

/** A bound number input for a `service_sla.sla_rules.{i}.{field}` path. */
function RuleNumber({
  index,
  field,
  label,
}: {
  index: number;
  field: 'response_time_minutes' | 'resolution_time_minutes';
  label: string;
}) {
  const { control } = useFormContext<ContractFormValues>();
  return (
    <Controller
      control={control}
      name={`service_sla.sla_rules.${index}.${field}` as const}
      render={({ field: f }) => (
        <Field label={label}>
          {(p) => (
            <NumericInput
              {...p}
              min={0}
              value={f.value == null ? '' : String(f.value)}
              onChange={(e) => f.onChange(Number(e.target.value))}
              onBlur={f.onBlur}
              name={f.name}
              ref={f.ref}
            />
          )}
        </Field>
      )}
    />
  );
}

/**
 * The editable `sla_rules` list — one row per ticket group with response /
 * resolution targets (minutes). Mirrors the legacy SLA rule builder; at least
 * one row is kept so the payload always carries a default rule.
 */
export function SlaRulesEditor() {
  const { control } = useFormContext<ContractFormValues>();
  const { fields, append, remove } = useFieldArray({ control, name: 'service_sla.sla_rules' });

  return (
    <div style={{ display: 'grid', gap: 'var(--vn-space-3)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <CardTitle>Support rules</CardTitle>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          startIcon={<Plus size={14} />}
          onClick={() =>
            append({
              ticket_type_group: 'default',
              response_time_minutes: 30,
              resolution_time_minutes: 240,
            })
          }
        >
          Add rule
        </Button>
      </div>

      {fields.map((f, index) => (
        <div
          key={f.id}
          style={{
            display: 'grid',
            gap: 'var(--vn-space-3)',
            gridTemplateColumns: 'minmax(160px, 1.2fr) 1fr 1fr auto',
            alignItems: 'end',
          }}
        >
          <Controller
            control={control}
            name={`service_sla.sla_rules.${index}.ticket_type_group` as const}
            render={({ field: rf }) => (
              <Field label="Ticket group">
                {(p) => (
                  <Input
                    {...p}
                    placeholder="default"
                    value={(rf.value as string) ?? ''}
                    onChange={rf.onChange}
                    onBlur={rf.onBlur}
                    name={rf.name}
                    ref={rf.ref}
                  />
                )}
              </Field>
            )}
          />
          <RuleNumber index={index} field="response_time_minutes" label="Response (min)" />
          <RuleNumber index={index} field="resolution_time_minutes" label="Resolution (min)" />
          <Button
            type="button"
            variant="ghost"
            size="sm"
            startIcon={<Trash2 size={14} />}
            disabled={fields.length <= 1}
            onClick={() => remove(index)}
            aria-label="Remove rule"
          />
        </div>
      ))}
    </div>
  );
}
